import React, { useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';

import FeaturedArticle from '@components/FeedItems/FeaturedArticle';
import Spinner from '@components/Spinner';
import useEventTracking from '@hooks/useEventTracking';
import type { Article } from '@interfaces/homepage';
import { impression } from '@tracking';
import { getUserRecommendedArticles } from '@utils/backend-api/articles';
import type { User } from '@utils/backend-api/currentUser';

import MyTISection from './Section';
import { MY_TI_ENGAGEMENT_LABELS, MY_TI_SECTION_IDS } from './constants';
import useRegisterSection from './useRegisterSection';

const MyTIRecommendedArticles = ({ initialCurrentUser }: { initialCurrentUser?: User }) => {
  const { trackEvent } = useEventTracking();
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const recommendedArticlesDisplayText = useMemo(() => {
    if (!initialCurrentUser) return MY_TI_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
    const isPro = initialCurrentUser?.is_pro_subscriber;

    return isPro
      ? MY_TI_ENGAGEMENT_LABELS.Engaged.Pro.RecommendedArticles
      : MY_TI_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
  }, [initialCurrentUser]);

  useEffect(() => {
    let cancelled = false;

    const fetchArticles = async () => {
      try {
        const results = await getUserRecommendedArticles();
        if (!cancelled) setArticles(results);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchArticles();

    return () => {
      cancelled = true;
    };
  }, []);

  useRegisterSection(MY_TI_SECTION_IDS.RecommendedArticles, isLoading || articles.length > 0);

  if (!isLoading && articles.length === 0) return null;

  return (
    <MyTISection
      id={MY_TI_SECTION_IDS.RecommendedArticles}
      highlight={recommendedArticlesDisplayText.label}
      subtitle={recommendedArticlesDisplayText.subtitle}
      title={recommendedArticlesDisplayText.title}
    >
      <InView
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: MY_TI_SECTION_IDS.RecommendedArticles }));
        }}
        triggerOnce
      >
        {isLoading ? (
          <Spinner marginY="small" />
        ) : (
          <div className="mt-32 flex flex-col gap-32 md:flex-row md:gap-112">
            {articles.map((article) => (
              <FeaturedArticle
                key={article.id}
                className="!border-0 !pb-0 [&_h2]:text-headline-sm"
                featuredArticle={article}
                isFluid
              />
            ))}
          </div>
        )}
      </InView>
    </MyTISection>
  );
};

export default MyTIRecommendedArticles;
